import type { ChunkManager } from '../world/ChunkManager.js';
import { chunkKey, worldToChunkCoord } from '../world/coords.js';
import type { GraphEdge, RoadGraph } from './roadGraph.js';
import { spawnWeight, TrafficTuning } from './tuning.js';

/**
 * Pure helpers used by TrafficCar — all stateless, all unit-tested via behavior in play.
 *
 * Coordinate convention throughout: +X east, +Z north. Yaw = atan2(x, z), so yaw=0 faces
 * +Z and increases clockwise toward +X. "Right" of a unit tangent (tx,tz) is (tz, -tx)
 * BEFORE applying the drive-on-right/left sign.
 */

/**
 * Pick the next edge to follow from an intersection node. Filters out the edge that would
 * take us straight back the way we came (no U-turns), then weights remaining edges by their
 * road class. If all outgoing edges would U-turn (degree-1 dead-end), we allow the U-turn
 * as a fallback so the car can escape.
 */
export function pickNextEdge(
  graph: RoadGraph,
  fromEdgeId: number,
  rng: () => number,
): GraphEdge | null {
  const fromEdge = graph.edges[fromEdgeId];
  if (!fromEdge) return null;
  const node = graph.nodes.get(fromEdge.toNode);
  if (!node) return null;

  const candidates: GraphEdge[] = [];
  let totalWeight = 0;
  for (const eid of node.outEdges) {
    const edge = graph.edges[eid];
    if (!edge) continue;
    // Skip the reverse of the edge we just traversed (U-turn) unless it's our only option.
    if (edge.toNode === fromEdge.fromNode && edge.wayId === fromEdge.wayId) continue;
    candidates.push(edge);
  }

  // Dead end? Allow the U-turn.
  if (candidates.length === 0) {
    for (const eid of node.outEdges) {
      const edge = graph.edges[eid];
      if (edge) candidates.push(edge);
    }
  }
  if (candidates.length === 0) return null;

  // Class-weighted random pick.
  for (const c of candidates) totalWeight += spawnWeight(c.roadClass);
  let roll = rng() * totalWeight;
  for (const c of candidates) {
    roll -= spawnWeight(c.roadClass);
    if (roll <= 0) return c;
  }
  return candidates[candidates.length - 1] ?? null;
}

/**
 * After a car exits the end node of its current edge, find the matching node in a neighbor
 * chunk's graph (same OSM nodeId, since OSM ids are globally unique). Returns the neighbor
 * graph + the matched node id, or null if no neighbor is built yet.
 */
export function findCrossChunkContinuation(
  chunks: ChunkManager,
  endNodeWorldX: number,
  endNodeWorldZ: number,
  endNodeId: number,
): { graph: RoadGraph; nodeId: number } | null {
  const frame = chunks.getFrame();
  if (!frame) return null;
  const coord = worldToChunkCoord(endNodeWorldX, endNodeWorldZ, frame.chunkSize);
  const chunk = chunks.getChunkByKey(chunkKey(coord));
  if (!chunk) return null;
  const graph = chunk.getRoadGraph();
  if (!graph) return null;
  if (graph.nodes.has(endNodeId)) return { graph, nodeId: endNodeId };
  return null;
}

/**
 * Compute the lateral lane offset for a given travel tangent. Two-way roads sit half a
 * lane-width off-center on the appropriate side; one-way roads stay on the centerline.
 *
 * "Right" of a travel tangent (tx,tz) in our +X-east/+Z-north convention is (tz, -tx). For
 * left-hand traffic (UK default), flip the sign.
 */
export function laneOffset(
  tx: number,
  tz: number,
  halfWidth: number,
  oneway: boolean,
): { x: number; z: number } {
  if (oneway) return { x: 0, z: 0 };
  const rightX = tz;
  const rightZ = -tx;
  const sign = TrafficTuning.driveOnRight ? 1 : -1;
  const off = halfWidth * TrafficTuning.laneOffsetFraction * sign;
  return { x: rightX * off, z: rightZ * off };
}

/**
 * Compute a pursuit target ~lookahead meters ahead of the car along its current path.
 *
 * Walks forward from the car's current position along the current edge, then continues
 * into successor edges as needed. Applies the lane offset at the final point so the car
 * naturally tracks the right (or left) side of the road. Returns null if the path runs out
 * (neighbor chunk not loaded) — caller should slow down.
 *
 * `currentEdge`: edge the car is currently on.
 * `tParam`: 0..1 progress along the current edge.
 * `successorPicker`: function that returns the next edge given the current edge — keeps
 *   this pure and lets the caller decide whether to peek (just for pursuit math, don't
 *   commit) or actually commit the route.
 */
export function pursuitTarget(
  currentEdge: GraphEdge,
  tParam: number,
  lookahead: number,
  successorPicker: (fromEdge: GraphEdge) => GraphEdge | null,
): { x: number; z: number; tangentX: number; tangentZ: number; edge: GraphEdge } | null {
  let edge = currentEdge;
  let remaining = lookahead;
  let t = tParam;

  // Distance left on the current edge.
  const distOnCurrent = (1 - t) * edge.length;
  if (remaining <= distOnCurrent) {
    const finalT = t + remaining / edge.length;
    const px = edge.x0 + (edge.x1 - edge.x0) * finalT;
    const pz = edge.z0 + (edge.z1 - edge.z0) * finalT;
    const off = laneOffset(edge.tx, edge.tz, edge.halfWidth, edge.oneway);
    return { x: px + off.x, z: pz + off.z, tangentX: edge.tx, tangentZ: edge.tz, edge };
  }

  // Walk to end of current, then into successors.
  remaining -= distOnCurrent;
  // Position at end of current edge:
  let baseX = edge.x1;
  let baseZ = edge.z1;
  while (remaining > 0) {
    const next = successorPicker(edge);
    if (!next) {
      // Can't continue — return what we have at the end of the last reachable edge,
      // offset for lane. Caller treats null specially; here we ALSO return null so
      // caller knows it should slow down.
      return null;
    }
    edge = next;
    t = 0;
    if (remaining <= edge.length) {
      const finalT = remaining / edge.length;
      const px = edge.x0 + (edge.x1 - edge.x0) * finalT;
      const pz = edge.z0 + (edge.z1 - edge.z0) * finalT;
      const off = laneOffset(edge.tx, edge.tz, edge.halfWidth, edge.oneway);
      return { x: px + off.x, z: pz + off.z, tangentX: edge.tx, tangentZ: edge.tz, edge };
    }
    remaining -= edge.length;
    baseX = edge.x1;
    baseZ = edge.z1;
  }

  // Defensive fallback (shouldn't reach here given the loop). Use the last endpoint.
  const off = laneOffset(edge.tx, edge.tz, edge.halfWidth, edge.oneway);
  return { x: baseX + off.x, z: baseZ + off.z, tangentX: edge.tx, tangentZ: edge.tz, edge };
}

/** Wrap an angle into [-π, π]. */
export function wrapPi(a: number): number {
  while (a > Math.PI) a -= 2 * Math.PI;
  while (a < -Math.PI) a += 2 * Math.PI;
  return a;
}

/** Yaw extracted from a unit quaternion. Returns angle in [-π, π], 0 = facing +Z. */
export function yawFromQuat(q: { x: number; y: number; z: number; w: number }): number {
  // Singularity-safe yaw for a near-upright rigid body. Formula derived from rotating
  // (0,0,1) by q and atan2-ing the resulting xz components.
  const x = 2 * (q.x * q.z + q.w * q.y);
  const z = 1 - 2 * (q.x * q.x + q.y * q.y);
  return Math.atan2(x, z);
}
